import React, { useEffect, useMemo, useState } from "react";
import "./AdminGiftsPanelV2.css";

const TOKEN_KEY = "evoAccessToken";

const EMPTY_FORM = {
  name: "",
  emoji: "🎁",
  description: "",
  coin_cost: "",
};

async function apiRequest(url, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY);

  const headers = {
    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers,
  });

  const raw = await response.text();

  let data = null;

  try {
    data = raw ? JSON.parse(raw) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    throw new Error(
      data?.error ||
      data?.message ||
      `Erreur serveur HTTP ${response.status}`
    );
  }

  return data;
}

function GiftFormModal({
  gift,
  onClose,
  onSaved,
}) {
  const editing = Boolean(gift?.id);

  const [form, setForm] = useState({
    name: gift?.name || "",
    emoji: gift?.emoji || "🎁",
    description: gift?.description || "",
    coin_cost: gift?.coin_cost
      ? String(gift.coin_cost)
      : "",
  });

  const [image, setImage] = useState(null);
  const [animation, setAnimation] = useState(null);
  const [removeAnimation, setRemoveAnimation] =
    useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const imagePreview = useMemo(() => {
    if (image) {
      return URL.createObjectURL(image);
    }

    return gift?.image_url || "";
  }, [image, gift]);

  const animationPreview = useMemo(() => {
    if (animation) {
      return URL.createObjectURL(animation);
    }

    return gift?.animation_url || "";
  }, [animation, gift]);

  function setField(name, value) {
    setForm((old) => ({
      ...old,
      [name]: value,
    }));
  }

  async function submit(e) {
    e.preventDefault();

    setError("");

    if (!form.name.trim()) {
      setError("Le nom du cadeau est obligatoire.");
      return;
    }

    const price = Number(form.coin_cost);

    if (!Number.isFinite(price) || price <= 0) {
      setError("Le prix doit être supérieur à 0 coin.");
      return;
    }

    if (!editing && !image) {
      setError(
        "Choisis une image d’aperçu pour le nouveau cadeau."
      );
      return;
    }

    const data = new FormData();

    data.append("name", form.name.trim());
    data.append("emoji", form.emoji || "");
    data.append(
      "description",
      form.description || ""
    );
    data.append("coin_cost", String(price));

    if (image) {
      data.append("image", image);
    }

    if (animation) {
      data.append("animation", animation);
    }

    if (removeAnimation) {
      data.append("remove_animation", "1");
    }

    setSaving(true);

    try {
      if (editing) {
        await apiRequest(
          `/api/admin/gifts/${gift.id}`,
          {
            method: "PUT",
            body: data,
          }
        );
      } else {
        await apiRequest(
          "/api/admin/gifts",
          {
            method: "POST",
            body: data,
          }
        );
      }

      await onSaved(
        editing
          ? "✅ Cadeau modifié avec succès."
          : "✅ Cadeau ajouté au catalogue."
      );
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="gift-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="gift-modal">
        <div className="gift-modal-header">
          <div>
            <h2>
              {editing
                ? "Modifier le cadeau"
                : "Ajouter un cadeau"}
            </h2>

            {editing && (
              <div className="gift-modal-id">
                Cadeau #{gift.id}
              </div>
            )}
          </div>

          <button
            type="button"
            className="gift-modal-close"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="gift-alert gift-alert-error">
            ❌ {error}
          </div>
        )}

        <form
          onSubmit={submit}
          className="gift-editor-form"
        >
          <div className="gift-editor-grid">
            <div className="gift-editor-fields">
              <label>
                Nom du cadeau
                <input
                  value={form.name}
                  onChange={(e) =>
                    setField(
                      "name",
                      e.target.value
                    )
                  }
                  placeholder="Ex : Robot EVO"
                />
              </label>

              <div className="gift-editor-row">
                <label>
                  Emoji
                  <input
                    value={form.emoji}
                    onChange={(e) =>
                      setField(
                        "emoji",
                        e.target.value
                      )
                    }
                    placeholder="🎁"
                  />
                </label>

                <label>
                  Prix en coins
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.coin_cost}
                    onChange={(e) =>
                      setField(
                        "coin_cost",
                        e.target.value
                      )
                    }
                    placeholder="300"
                  />
                </label>
              </div>

              <label>
                Description
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setField(
                      "description",
                      e.target.value
                    )
                  }
                  placeholder="Description du cadeau"
                />
              </label>
            </div>

            <div className="gift-editor-media">
              <div className="gift-upload-box">
                <div className="gift-upload-title">
                  🖼 Image d’aperçu
                </div>

                {imagePreview ? (
                  <img
                    className="gift-editor-image-preview"
                    src={imagePreview}
                    alt="Aperçu"
                  />
                ) : (
                  <div className="gift-editor-empty-preview">
                    🎁
                  </div>
                )}

                <label className="gift-file-button">
                  {editing
                    ? "Changer l’image"
                    : "Choisir une image"}

                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
                    onChange={(e) =>
                      setImage(
                        e.target.files?.[0] ||
                          null
                      )
                    }
                  />
                </label>

                {image && (
                  <div className="gift-selected-file">
                    {image.name}
                  </div>
                )}
              </div>

              <div className="gift-upload-box">
                <div className="gift-upload-title">
                  🎬 Animation MP4
                </div>

                {animationPreview &&
                !removeAnimation ? (
                  <video
                    className="gift-editor-video-preview"
                    src={animationPreview}
                    controls
                    muted
                    playsInline
                  />
                ) : (
                  <div className="gift-editor-empty-preview">
                    MP4
                  </div>
                )}

                <label className="gift-file-button">
                  {gift?.animation_url
                    ? "Remplacer le MP4"
                    : "Choisir un MP4"}

                  <input
                    type="file"
                    accept="video/mp4,.mp4"
                    onChange={(e) => {
                      setAnimation(
                        e.target.files?.[0] ||
                          null
                      );
                      setRemoveAnimation(false);
                    }}
                  />
                </label>

                {animation && (
                  <div className="gift-selected-file">
                    {animation.name}
                  </div>
                )}

                {editing &&
                  gift?.animation_url && (
                    <label className="gift-remove-mp4">
                      <input
                        type="checkbox"
                        checked={
                          removeAnimation
                        }
                        onChange={(e) =>
                          setRemoveAnimation(
                            e.target.checked
                          )
                        }
                      />

                      Retirer le MP4
                    </label>
                  )}
              </div>
            </div>
          </div>

          <div className="gift-modal-footer">
            <button
              type="button"
              className="gift-btn gift-btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Annuler
            </button>

            <button
              type="submit"
              className="gift-btn gift-btn-save"
              disabled={saving}
            >
              {saving
                ? "Enregistrement…"
                : editing
                ? "💾 Enregistrer les modifications"
                : "➕ Ajouter au catalogue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminGiftsPanel() {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] =
    useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] =
    useState("");

  const [modalGift, setModalGift] =
    useState(null);
  const [modalOpen, setModalOpen] =
    useState(false);

  const [filter, setFilter] =
    useState("all");
  const [search, setSearch] =
    useState("");

  async function load() {
    setLoading(true);
    setError("");

    try {
      const data = await apiRequest(
        "/api/admin/gifts"
      );

      setGifts(
        Array.isArray(data) ? data : []
      );
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function addGift() {
    setModalGift(null);
    setModalOpen(true);
    setError("");
    setSuccess("");
  }

  function editGift(gift) {
    setModalGift(gift);
    setModalOpen(true);
    setError("");
    setSuccess("");
  }

  async function afterSaved(message) {
    setModalOpen(false);
    setModalGift(null);
    setSuccess(message);
    setError("");
    await load();
  }

  async function toggleGift(gift) {
    const active =
      Number(gift.is_active ?? 1) === 1;

    const confirmText = active
      ? `Retirer "${gift.name}" du catalogue ?`
      : `Remettre "${gift.name}" dans le catalogue ?`;

    if (!window.confirm(confirmText)) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      await apiRequest(
        `/api/admin/gifts/${gift.id}/active`,
        {
          method: "PATCH",
          headers: {
            "Content-Type":
              "application/json",
          },
          body: JSON.stringify({
            is_active: !active,
          }),
        }
      );

      setSuccess(
        active
          ? `⏸ ${gift.name} a été retiré du catalogue.`
          : `✅ ${gift.name} a été remis dans le catalogue.`
      );

      await load();
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  }

  const visibleGifts = useMemo(() => {
    const q = search
      .trim()
      .toLowerCase();

    return gifts.filter((gift) => {
      const active =
        Number(
          gift.is_active ?? 1
        ) === 1;

      if (
        filter === "active" &&
        !active
      ) {
        return false;
      }

      if (
        filter === "removed" &&
        active
      ) {
        return false;
      }

      if (
        filter === "mp4" &&
        !gift.animation_url
      ) {
        return false;
      }

      if (q) {
        const text =
          `${gift.name || ""} ${
            gift.description || ""
          } ${gift.id || ""}`
            .toLowerCase();

        if (!text.includes(q)) {
          return false;
        }
      }

      return true;
    });
  }, [gifts, filter, search]);

  const activeCount = gifts.filter(
    (g) =>
      Number(g.is_active ?? 1) === 1
  ).length;

  const mp4Count = gifts.filter(
    (g) => Boolean(g.animation_url)
  ).length;

  return (
    <div className="gifts-admin-page">
      <div className="gifts-admin-header">
        <div>
          <h1>🎁 Gestion des cadeaux</h1>
          <p>
            Gère directement le catalogue
            EVO-LIVE
          </p>
        </div>

        <button
          type="button"
          className="gift-btn gift-btn-add"
          onClick={addGift}
        >
          ＋ Ajouter un cadeau
        </button>
      </div>

      <div className="gifts-admin-stats">
        <div className="gift-stat-card">
          <strong>
            {gifts.length}
          </strong>
          <span>Total</span>
        </div>

        <div className="gift-stat-card">
          <strong>
            {activeCount}
          </strong>
          <span>Actifs</span>
        </div>

        <div className="gift-stat-card">
          <strong>
            {mp4Count}
          </strong>
          <span>Animés MP4</span>
        </div>
      </div>

      {success && (
        <div className="gift-alert gift-alert-success">
          {success}
        </div>
      )}

      {error && (
        <div className="gift-alert gift-alert-error">
          ❌ {error}
        </div>
      )}

      <div className="gifts-admin-toolbar">
        <input
          className="gift-search"
          type="search"
          placeholder="🔎 Rechercher un cadeau…"
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
        />

        <div className="gift-filters">
          {[
            ["all", "Tous"],
            ["active", "Actifs"],
            ["mp4", "MP4"],
            ["removed", "Retirés"],
          ].map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={
                filter === value
                  ? "active"
                  : ""
              }
              onClick={() =>
                setFilter(value)
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="gifts-admin-loading">
          Chargement du catalogue…
        </div>
      ) : visibleGifts.length === 0 ? (
        <div className="gifts-admin-empty">
          Aucun cadeau trouvé.
        </div>
      ) : (
        <div className="gifts-admin-grid">
          {visibleGifts.map((gift) => {
            const active =
              Number(
                gift.is_active ?? 1
              ) === 1;

            return (
              <article
                className={`gift-admin-card ${
                  !active
                    ? "gift-admin-card-disabled"
                    : ""
                }`}
                key={gift.id}
              >
                <div className="gift-card-top">
                  <span className="gift-id">
                    #{gift.id}
                  </span>

                  <span
                    className={`gift-state ${
                      active
                        ? "on"
                        : "off"
                    }`}
                  >
                    {active
                      ? "ACTIF"
                      : "RETIRÉ"}
                  </span>
                </div>

                <div className="gift-card-preview">
                  {gift.image_url ? (
                    <img
                      src={gift.image_url}
                      alt={gift.name}
                    />
                  ) : (
                    <div className="gift-no-preview">
                      🎁
                    </div>
                  )}

                  {gift.animation_url && (
                    <span className="gift-mp4-badge">
                      🎬 MP4
                    </span>
                  )}
                </div>

                <div className="gift-card-body">
                  <h3>
                    {gift.emoji || "🎁"}{" "}
                    {gift.name}
                  </h3>

                  <div className="gift-price-big">
                    {gift.coin_cost}{" "}
                    <span>coins</span>
                  </div>

                  {gift.description && (
                    <p>
                      {gift.description}
                    </p>
                  )}
                </div>

                <div className="gift-card-actions">
                  <button
                    type="button"
                    className="gift-btn gift-btn-edit"
                    onClick={() =>
                      editGift(gift)
                    }
                  >
                    ✏️ Modifier
                  </button>

                  <button
                    type="button"
                    className={`gift-btn ${
                      active
                        ? "gift-btn-remove"
                        : "gift-btn-restore"
                    }`}
                    onClick={() =>
                      toggleGift(gift)
                    }
                  >
                    {active
                      ? "Retirer"
                      : "Remettre"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <GiftFormModal
          gift={modalGift}
          onClose={() => {
            setModalOpen(false);
            setModalGift(null);
          }}
          onSaved={afterSaved}
        />
      )}
    </div>
  );
}
